import { t, formatDate } from "../core/i18n.js";
import { runtime } from "../core/runtime.js";
import { esc, persist, dateKey } from "../core/model.js";
import {
  profile,
  planningGroups,
  plansIn,
  planningGroup,
  preservePlanHistory,
} from "../core/planning.js";
import {
  DEFAULT_GROUPS,
  DAYS,
  weekDates,
  weekStart,
  parseLocalDate,
  reminderWeek,
  validPlanningProfile,
} from "../../shared/planning.js";
import { loadJSON, saveJSON } from "../core/storage.js";
import { getSyncInfo } from "../services/storage.js";
import { closeModal } from "./plans.js";
import { render } from "./views.js";
import { toast } from "../core/navigation.js";
import { icon } from "./icons.js";
import {
  planExamples,
  hasExample,
  renderPlanExamples,
  exampleBulkPlaceholder,
} from "./plan-examples.js";

const modes = [
  [
    "daily",
    "Same plan every day",
    "One routine, seven days. Repeats automatically.",
    "reset",
  ],
  [
    "groups",
    "Different day groups",
    "Weekdays and weekends, or any days you choose.",
    "manage",
  ],
  [
    "weekly",
    "Plan each week",
    "A separate plan for each date. A fresh start every week.",
    "weekly",
  ],
];
let draft = null;
const uid = () => "plan_" + crypto.randomUUID();
const groupName = (group) =>
  group.name ||
  t(
    group.id === "Weekdays"
      ? "Mon–Fri"
      : group.id === "Weekend"
        ? "Sat–Sun"
        : "Every day",
  );
export const planningModeLabel = () =>
  t(
    modes.find(([mode]) => mode === profile().mode)?.[1] ||
      "Different day groups",
  );
export function planningSettings() {
  return `<div class="panel setting-section planning-setting"><h3>${t("Planning setup")}</h3><p>${planningModeLabel()}</p><button class="btn black" data-planning-setup>${t("Change my rhythm")}</button></div>`;
}
const weekLabel = (key) => {
  const days = weekDates(parseLocalDate(key));
  return `${formatDate(parseLocalDate(days[0]), { day: "numeric", month: "short" })} – ${formatDate(parseLocalDate(days[6]), { day: "numeric", month: "short", year: "numeric" })}`;
};

function buildMode(mode, week) {
  const current = profile();
  const groups =
    mode === "daily"
      ? [{ id: "AllDays", name: "", days: [0, 1, 2, 3, 4, 5, 6] }]
      : structuredClone(
          current.mode === "groups" ? current.groups : DEFAULT_GROUPS,
        );
  const plans = {};
  if (mode === "weekly") {
    for (const key of weekDates(parseLocalDate(week))) {
      plans["date:" + key] = structuredClone(
        runtime.state.custom.planHistory?.[key] ||
          (current.mode === "weekly"
            ? runtime.state.custom.datedPlans?.[key] || []
            : plansIn(planningGroup(parseLocalDate(key)))),
      );
    }
  } else {
    for (const group of groups) {
      const sample = parseLocalDate(week);
      sample.setDate(sample.getDate() + group.days[0]);
      plans[group.id] = structuredClone(
        runtime.state.templates[group.id] || plansIn(planningGroup(sample)),
      );
    }
  }
  return { groups, plans };
}

export function openPlanningSetup({ week = null, step = 0 } = {}) {
  const mode = profile().mode;
  week ||= weekStart(new Date());
  draft = {
    mode,
    week,
    step,
    active:
      mode === "weekly" && weekStart(new Date()) === week
        ? "date:" + dateKey(new Date())
        : null,
    error: "",
    examplesOpen: {},
    cache: {},
    ...buildMode(mode, week),
  };
  paintSetup();
}

function currentTabs() {
  return draft.mode === "weekly"
    ? weekDates(parseLocalDate(draft.week)).map((key, index) => ({
        id: "date:" + key,
        name: t(DAYS[index]),
        date: key,
      }))
    : draft.groups;
}
function activeTab() {
  const tabs = currentTabs();
  if (!tabs.some((group) => group.id === draft.active))
    draft.active = tabs[0].id;
  return tabs.find((group) => group.id === draft.active);
}
function paintSetup(focus = null) {
  if (!draft) return;
  const host = document.getElementById("modal");
  const tab = activeTab();
  const tasks = (draft.plans[tab.id] ||= []);
  host.innerHTML = `<div class="modal planning-modal" role="dialog" aria-modal="true" aria-labelledby="planningTitle"><header class="planning-modal-header"><span class="planning-wordmark">DayX <span>/ ${t("YOUR RHYTHM")}</span></span><button type="button" class="planning-close" data-close aria-label="${t("Close")}">${icon("close")}</button></header><div class="planning-step"><i class="${draft.step === 0 ? "active" : "done"}"></i><i class="${draft.step === 1 ? "active" : ""}"></i><span>${t("Step {n} of 2", { n: draft.step + 1 })}</span></div>
      <h2 id="planningTitle">${t(draft.step === 0 ? "How do your days work?" : draft.mode === "weekly" ? "Make space for your week." : "Build a routine that fits.")}</h2><p class="planning-intro">${t(draft.step === 0 ? "Choose a starting point. You can change it anytime." : draft.mode === "weekly" ? "Give each day its own priorities. Empty days can be rest days." : "Group your days, then add what matters. Your plans repeat each week.")}</p>
      ${
        draft.step === 0
          ? `<div class="planning-modes">${modes.map(([mode, title, description, glyph], index) => `<button type="button" class="planning-mode ${draft.mode === mode ? "selected" : ""}" data-planning-mode="${mode}" aria-pressed="${draft.mode === mode}"><span class="planning-mode-icon">${icon(glyph)}</span><span><strong>${t(title)}</strong><small>${t(description)}</small></span><i>${draft.mode === mode ? icon("check") : String(index + 1).padStart(2, "0")}</i></button>`).join("")}</div>`
          : `<div class="planning-editor">
        ${draft.mode === "weekly" ? `<div class="planning-week-picker"><label>${t("Week starting")}<input type="date" data-planning-week value="${draft.week}"></label><strong>${esc(weekLabel(draft.week))}</strong><button class="btn" type="button" data-copy-week>${icon("reset")}${t("Copy previous week")}</button></div>` : draft.mode === "groups" ? `<details class="planning-groups"><summary>${icon("manage")}<span>${t("Customize day groups")}</span><small>${t("Choose any days together")}</small></summary><div class="planning-group-editor">${draft.groups.map((group) => `<div class="planning-group-row"><div><input aria-label="${t("Group name")}" data-group-name="${esc(group.id)}" maxlength="80" value="${esc(groupName(group))}"><button type="button" data-remove-group="${esc(group.id)}" aria-label="${t("Remove group")}" ${draft.groups.length === 1 ? "disabled" : ""}>${icon("trash")}</button></div><div class="planning-day-chips">${DAYS.map((day, index) => `<button type="button" data-group-day="${esc(group.id)}" data-day-index="${index}" aria-label="${t(day)}" aria-pressed="${group.days.includes(index)}" title="${t(day)}">${t(day).slice(0, 3)}</button>`).join("")}</div></div>`).join("")}<button class="planning-text-button" type="button" data-add-group ${draft.groups.length === 7 ? "disabled" : ""}>${icon("plus")}${t("Add day group")}</button><p>${t("Each day belongs to one group. Selecting it moves it here.")}</p></div></details>` : `<div class="planning-repeat-note">${icon("reset")}<span>${t("Your plan repeats every day, including weekends.")}</span></div>`}
        <div class="planning-tabs" role="group" aria-label="${t("Choose plan group")}">${currentTabs()
          .map(
            (group) =>
              `<button type="button" data-planning-tab="${esc(group.id)}" aria-pressed="${draft.active === group.id}"><span>${esc(draft.mode === "weekly" ? group.name : groupName(group))}</span>${group.date ? `<small>${parseLocalDate(group.date).getDate()}</small>` : `<small>${group.days.map((index) => t(DAYS[index]).slice(0, 3)).join(" · ")}</small>`}</button>`,
          )
          .join("")}</div>
        ${renderPlanExamples(tasks, draft.examplesOpen[tab.id] ?? tasks.length === 0)}
        <div class="planning-task-header"><strong>${t("Your priorities")}</strong><span>${t("{n} plans", { n: tasks.filter((task) => task.name.trim()).length })}</span></div><div class="planning-task-list">${tasks.length ? tasks.map((task, index) => `<div class="planning-task-row"><span>${String(index + 1).padStart(2, "0")}</span><input data-draft-name="${index}" aria-label="${t("Plan name")} ${index + 1}" maxlength="160" value="${esc(task.name)}" placeholder="${t("What would you like to do?")}"><input data-draft-time="${index}" aria-label="${t("Time / schedule")} ${index + 1}" maxlength="120" value="${esc(task.schedule || "")}" placeholder="${t("Time (optional)")}"><button type="button" data-remove-draft="${index}" aria-label="${t("Delete {name}", { name: esc(task.name || t("Plan")) })}">${icon("close")}</button></div>`).join("") : `<div class="planning-empty">${icon("today")}<span>${t("Start with one priority.")}</span><small>${t("A small plan is a good beginning.")}</small></div>`}</div>
        <button type="button" class="planning-add" data-add-draft>${icon("plus")}${t("Add a plan")}</button><details class="planning-bulk"><summary>${t("Add several plans at once")}</summary><p>${t("One plan per line. You can start with a time: 07:00 Read.")}</p><textarea rows="4" maxlength="50000" data-bulk-plans aria-label="${t("Plan names, one per line")}" placeholder="${esc(exampleBulkPlaceholder())}"></textarea><button class="btn" type="button" data-add-bulk>${t("Add these plans")}</button></details>
      </div>`
      }
      <p class="planning-error" role="alert">${draft.error ? t(draft.error) : ""}</p><footer class="planning-modal-footer"><button type="button" class="planning-text-button" ${draft.step === 0 ? "data-close" : "data-planning-back"}>${t(draft.step === 0 ? "Maybe later" : "Back")}</button><button type="button" class="btn black" ${draft.step === 0 ? "data-planning-next" : "data-planning-save"}>${t(draft.step === 0 ? "Continue" : "Save my plans")}${icon("arrow")}</button></footer><p class="planning-saved-note">${icon("check")}${t("Your pace. Your priorities. Always editable.")}</p></div>`;
  host.classList.add("show");
  bindSetup(host);
  if (focus) host.querySelector(focus)?.focus();
}

function bindSetup(host) {
  host
    .querySelector(".planning-examples")
    ?.addEventListener("toggle", (event) => {
      if (draft && event.currentTarget.isConnected)
        draft.examplesOpen[draft.active] = event.currentTarget.open;
    });
  const addExamples = (examples) => {
    const tasks = draft.plans[draft.active],
      first = tasks.length;
    for (const example of examples) {
      if (tasks.length >= 1000) break;
      if (hasExample(tasks, example)) continue;
      tasks.push({
        id: `plan_example_${example.key}_${crypto.randomUUID()}`,
        name: t(example.name),
        schedule: example.schedule,
        notes: "",
        study: false,
      });
    }
    draft.examplesOpen[draft.active] = true;
    paintSetup(`[data-draft-name="${first}"]`);
  };
  host.querySelectorAll("[data-add-example]").forEach((button) => {
    button.onclick = () =>
      addExamples(
        planExamples.filter(
          (example) => example.key === button.dataset.addExample,
        ),
      );
  });
  host
    .querySelector("[data-add-all-examples]")
    ?.addEventListener("click", () => addExamples(planExamples));
  host.querySelectorAll("[data-planning-mode]").forEach(
    (button) =>
      (button.onclick = () => {
        draft.cache[draft.mode] = { groups: draft.groups, plans: draft.plans };
        draft.mode = button.dataset.planningMode;
        Object.assign(
          draft,
          draft.cache[draft.mode] || buildMode(draft.mode, draft.week),
        );
        draft.active =
          draft.mode === "weekly" && weekStart(new Date()) === draft.week
            ? "date:" + dateKey(new Date())
            : null;
        paintSetup(`[data-planning-mode="${draft.mode}"]`);
      }),
  );
  host.querySelector("[data-planning-next]")?.addEventListener("click", () => {
    draft.step = 1;
    paintSetup();
  });
  host.querySelector("[data-planning-back]")?.addEventListener("click", () => {
    draft.step = 0;
    paintSetup();
  });
  host.querySelectorAll("[data-planning-tab]").forEach(
    (button) =>
      (button.onclick = () => {
        draft.active = button.dataset.planningTab;
        paintSetup(`[data-planning-tab="${draft.active}"]`);
      }),
  );
  host.querySelectorAll("[data-group-name]").forEach(
    (input) =>
      (input.oninput = () => {
        draft.groups.find(
          (group) => group.id === input.dataset.groupName,
        ).name = input.value;
      }),
  );
  host.querySelectorAll("[data-group-day]").forEach(
    (button) =>
      (button.onclick = () => {
        const day = +button.dataset.dayIndex,
          id = button.dataset.groupDay;
        for (const group of draft.groups)
          group.days = group.days.filter((index) => index !== day);
        draft.groups.find((group) => group.id === id).days.push(day);
        draft.groups.forEach((group) => group.days.sort((a, b) => a - b));
        paintSetup();
        host.querySelector(".planning-groups").open = true;
      }),
  );
  host.querySelector("[data-add-group]")?.addEventListener("click", () => {
    const id = "group_" + crypto.randomUUID();
    draft.groups.push({
      id,
      name: t("Group {n}", { n: draft.groups.length + 1 }),
      days: [],
    });
    draft.plans[id] = [];
    paintSetup();
    host.querySelector(".planning-groups").open = true;
    host.querySelector(`[data-group-name="${id}"]`).focus();
  });
  host.querySelectorAll("[data-remove-group]").forEach(
    (button) =>
      (button.onclick = () => {
        const removed = draft.groups.find(
          (group) => group.id === button.dataset.removeGroup,
        );
        draft.groups = draft.groups.filter((group) => group !== removed);
        draft.groups[0].days.push(...removed.days);
        draft.groups[0].days.sort((a, b) => a - b);
        draft.active = null;
        paintSetup();
        host.querySelector(".planning-groups").open = true;
      }),
  );
  host.querySelectorAll("[data-draft-name]").forEach(
    (input) =>
      (input.oninput = () => {
        draft.plans[draft.active][+input.dataset.draftName].name = input.value;
      }),
  );
  host.querySelectorAll("[data-draft-time]").forEach(
    (input) =>
      (input.oninput = () => {
        draft.plans[draft.active][+input.dataset.draftTime].schedule =
          input.value;
      }),
  );
  host.querySelectorAll("[data-remove-draft]").forEach(
    (button) =>
      (button.onclick = () => {
        draft.plans[draft.active].splice(+button.dataset.removeDraft, 1);
        paintSetup();
      }),
  );
  host.querySelector("[data-add-draft]")?.addEventListener("click", () => {
    const tasks = draft.plans[draft.active];
    if (tasks.length >= 1000) return;
    tasks.push({ id: uid(), name: "", schedule: "", notes: "", study: false });
    paintSetup(`[data-draft-name="${tasks.length - 1}"]`);
  });
  host.querySelectorAll("[data-draft-name],[data-draft-time]").forEach(
    (input) =>
      (input.onkeydown = (event) => {
        if (event.key === "Enter") {
          event.preventDefault();
          host.querySelector("[data-add-draft]").click();
        }
      }),
  );
  host.querySelector("[data-add-bulk]")?.addEventListener("click", () => {
    const tasks = draft.plans[draft.active],
      lines = host
        .querySelector("[data-bulk-plans]")
        .value.split(/\r?\n/)
        .map((line) => line.trim())
        .filter(Boolean);
    for (const line of lines.slice(0, 1000 - tasks.length)) {
      const match = line.match(/^(\d{1,2}:\d{2})\s+(?:[|–-]\s*)?(.+)$/);
      tasks.push({
        id: uid(),
        name: (match?.[2] || line).slice(0, 160),
        schedule: match?.[1] || "",
        notes: "",
        study: false,
      });
    }
    paintSetup();
  });
  host
    .querySelector("[data-planning-week]")
    ?.addEventListener("change", (event) => {
      if (!event.target.value) return;
      const next = weekStart(parseLocalDate(event.target.value));
      draft.cache["week:" + draft.week] = draft.plans;
      draft.week = next;
      draft.plans =
        draft.cache["week:" + next] || buildMode("weekly", next).plans;
      draft.active = null;
      paintSetup();
    });
  host.querySelector("[data-copy-week]")?.addEventListener("click", () => {
    const date = parseLocalDate(draft.week);
    date.setDate(date.getDate() - 7);
    const previous = weekDates(date),
      current = weekDates(parseLocalDate(draft.week));
    current.forEach((key, index) => {
      if (!draft.plans["date:" + key].length)
        draft.plans["date:" + key] = structuredClone(
          draft.cache["week:" + weekStart(date)]?.["date:" + previous[index]] ||
            plansIn("date:" + previous[index]),
        );
    });
    paintSetup();
    toast(t("Previous week copied into empty days."));
  });
  host
    .querySelector("[data-planning-save]")
    ?.addEventListener("click", saveSetup);
}

function saveSetup() {
  const next = {
    version: 1,
    configured: true,
    mode: draft.mode,
    groups: structuredClone(draft.groups),
    readyWeeks: profile().readyWeeks || [],
  };
  if (!validPlanningProfile(next)) {
    draft.error = "Give every group at least one day.";
    paintSetup();
    const groups = document.querySelector(".planning-groups");
    if (groups) groups.open = true;
    return;
  }
  const pendingPlans =
    draft.mode === "weekly"
      ? Object.assign(
          {},
          ...Object.entries(draft.cache)
            .filter(([key]) => key.startsWith("week:"))
            .map(([, plans]) => plans),
          draft.plans,
        )
      : draft.plans;
  for (const tasks of Object.values(pendingPlans))
    if (tasks.some((task) => !task.name.trim() && task.schedule?.trim())) {
      draft.error = "Give your timed plan a name.";
      paintSetup();
      return;
    }
  preservePlanHistory();
  for (const [group, tasks] of Object.entries(pendingPlans)) {
    const cleaned = tasks
      .filter((task) => task.name.trim())
      .map((task) => ({
        ...task,
        name: task.name.trim(),
        schedule: (task.schedule || "").trim(),
      }));
    if (group.startsWith("date:")) {
      const key = group.slice(5);
      if (runtime.state.custom.planHistory?.[key])
        runtime.state.custom.planHistory[key] = cleaned;
      else (runtime.state.custom.datedPlans ||= {})[key] = cleaned;
    } else runtime.state.templates[group] = cleaned;
  }
  if (draft.mode === "weekly")
    next.readyWeeks = [
      ...new Set([
        ...next.readyWeeks,
        draft.week,
        ...Object.keys(draft.cache)
          .filter((key) => key.startsWith("week:"))
          .map((key) => key.slice(5)),
      ]),
    ].slice(-1000);
  runtime.state.prefs.planning = next;
  runtime.selectedDate = parseLocalDate(
    draft.mode === "weekly" ? draft.week : dateKey(new Date()),
  );
  runtime.manageDay = planningGroups()[0].id;
  runtime.tableGroup = runtime.manageDay;
  runtime.tablePage = 0;
  persist();
  draft = null;
  closeModal();
  render();
  toast(t("Your plans are ready."));
}

export function maybePromptPlanning() {
  if (
    document.body.dataset.presentation !== "workspace" ||
    !runtime.state.prefs ||
    document.getElementById("modal").classList.contains("show") ||
    getSyncInfo().requiresLogin
  )
    return;
  const week = runtime.state.prefs.planning?.configured
    ? reminderWeek(runtime.state.prefs, new Date())
    : "setup";
  if (!week) return;
  const dismissed = loadJSON("dayx:planning-dismissed", null);
  if (dismissed?.date === dateKey(new Date()) && dismissed?.week === week)
    return;
  openPlanningSetup(week === "setup" ? {} : { week, step: 1 });
}
export function planningNotice() {
  const unconfigured = !runtime.state.prefs.planning?.configured;
  const week = unconfigured
    ? null
    : reminderWeek(runtime.state.prefs, new Date());
  if (!unconfigured && !week) return "";
  return `<section class="planning-notice"><span>${icon(unconfigured ? "spark" : "weekly")}</span><div><strong>${t(unconfigured ? "Make this planner yours." : "Your next week starts here.")}</strong><small>${unconfigured ? t("Choose how your days repeat.") : esc(weekLabel(week))}</small></div><button type="button" data-planning-setup ${week ? `data-planning-target="${week}"` : ""}>${t(unconfigured ? "Choose my rhythm" : "Plan this week")}${icon("arrow")}</button></section>`;
}
document.addEventListener("dayx:modal-closed", () => {
  if (!draft) return;
  const week = runtime.state.prefs.planning?.configured
    ? reminderWeek(runtime.state.prefs, new Date())
    : "setup";
  saveJSON("dayx:planning-dismissed", { date: dateKey(new Date()), week });
  draft = null;
});
window.addEventListener("dayx:language", () => {
  if (draft) paintSetup();
});
